import constructorSeed from './seeds/constructortechcommunity.connect.json';
export const ENABLE_CONNECT_LANDING = 'ENABLE_CONNECT_LANDING';

export const DEFAULT_SEARCH_PLACEHOLDER = 'Search for person or expertise';

/** English default follows the UI language. A custom admin placeholder stays as saved. */
export const resolveConnectSearchPlaceholder = (configured, localized) => {
    const value = typeof configured === 'string' ? configured.trim() : '';
    if (!value || value === DEFAULT_SEARCH_PLACEHOLDER) {
        const translated = typeof localized === 'string' ? localized.trim() : '';
        return translated || DEFAULT_SEARCH_PLACEHOLDER;
    }
    return value;
};

export const DEFAULT_CONNECT_THEME = {
    bg: '#0C0E12',
    cardBg: '#13161B',
    text: '#ffffff',
    muted: '#a1a1aa',
    // Soft electric blue washes — animated hero aurora
    aurora1: '#1e6bff',
    aurora2: '#3b82f6',
    aurora3: '#2563eb',
    // Missing key stays on so existing landings keep the glow.
    auroraEnabled: true,
    highlightColor: '#68bbff',
    // Legacy keys kept for backward compatibility
    highlightFrom: '#38bdf8',
    highlightTo: '#67e8f9',
    btnBg: '#ffffff',
    btnText: '#0a0a0a',
    loginBtnBg: '#ffffff',
    loginBtnText: '#0a0a0a',
    loginBtnHoverBg: '#e5e5e5',
    secondaryBtnBg: 'transparent',
    secondaryBtnText: '#ffffff',
    secondaryBtnHoverBg: 'rgba(255,255,255,0.1)',
    secondaryBtnHoverText: '#ffffff',
    secondaryBtnBorder: 'rgba(255,255,255,0.4)',
    searchBorder: 'rgba(255,255,255,0.2)',
    searchBorderFocus: 'rgba(255,255,255,0.4)',
    cardBorder: 'rgba(255,255,255,0.25)',
    cardBorderHover: 'rgba(255,255,255,0.5)',
    cardTitleHover: '#ffffff',
    processCheckBg: '#22c55e',
    processItemBg: 'rgba(0,0,0,0.4)',
    processItemText: '#a1a1aa',
    processItemBorder: 'rgba(255,255,255,0.4)',
    processItemIcon: '#16a34a',
    badgeBg: '#3b82f6',
    badgeText: '#ffffff',
    tags: ['#3b82f6', '#16a34a', '#7c3aed', '#e11d48'],
};

const DEFAULT_HERO_SEARCH = {
    enabled: false,
    placeholder: DEFAULT_SEARCH_PLACEHOLDER,
    tags: [],
};

const DEFAULT_SECTIONS = {
    hero: {
        title: 'Connect with your next career mentor',
        highlight: 'career mentor',
        subtitle:
            'A private alumni network for meaningful introductions, shared opportunities, and community-led events — built around the people you already trust.',
        loginButtonText: 'Log In',
        secondaryCta: { text: 'See how it works', href: '#connect-how' },
        primaryCta: { text: 'Join Constructor Connect', action: 'register' },
        search: DEFAULT_HERO_SEARCH,
    },
    carousel: { cards: [] },
    process: {
        sectionTitle: 'How suggestions and introductions work',
        sectionSubtitle:
            'Choose a path that fits your goal — search, review ideas, or follow up after events.',
        title: 'Not every connection has to start the same way',
        items: [],
        steps: [],
    },
    faq: { title: 'Common questions', items: [] },
    // cards are loaded live from events/vacancies APIs — only title/exploreLink come from config
    events: {
        title: 'Find your next reason to connect',
        exploreLink: '/events',
        emptyText: 'No upcoming events yet.',
        exploreText: 'Explore events and resources',
        paidTagText: 'Paid',
    },
    opportunities: {
        title: 'Opportunities shared through your community',
        exploreLink: '/jobs',
        emptyText: 'No opportunities yet.',
        exploreText: 'Explore all career opportunities',
        viewJobText: 'View job',
    },
    cta: {
        title: 'Join Constructor Connect',
        subtitle:
            'Create your profile, share what you are looking for, and start meeting people who can help you grow.',
        buttonText: 'Join community',
        emailPlaceholder: 'Enter your email',
        policyPrefixText: 'We care about your data in our',
        policyLinkText: 'privacy policy',
        policyLinkHref: '/privacy',
        footerCopyrightText: 'All rights reserved.',
    },
};

const CONNECT_SEED_BY_DOMAIN = {
    constructortechcommunity: constructorSeed,
};

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const mergeNested = (base, override) => {
    if (Array.isArray(override)) return override;
    if (!isPlainObject(base) || !isPlainObject(override)) {
        return override === undefined ? base : override;
    }

    const result = { ...base };
    Object.keys(override).forEach((key) => {
        result[key] = mergeNested(base[key], override[key]);
    });
    return result;
};

const resolveSeedConfig = (community, parsedConfig) => {
    const domainSeed = CONNECT_SEED_BY_DOMAIN[community?.domain];
    if (domainSeed) return domainSeed;

    const normalizedBrandName = String(parsedConfig?.brandName || community?.name || '')
        .trim()
        .toLowerCase();
    if (normalizedBrandName === 'constructor') return constructorSeed;

    return {};
};

const extractOptionType = (option) => {
    if (typeof option === 'string') return option;
    return option?.optionType || option?.type || '';
};

const extractOptionValue = (option) => {
    if (!option || typeof option === 'string') return '';
    const value = option.optionValue ?? option.value;
    return typeof value === 'string' ? value.trim() : '';
};

const safeParseJson = (value) => {
    if (!value || typeof value !== 'string') return {};
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const normalizeHeroSearch = (search) => {
    const merged = {
        ...DEFAULT_HERO_SEARCH,
        ...(isPlainObject(search) ? search : {}),
    };
    const placeholder =
        typeof merged.placeholder === 'string' && merged.placeholder.trim()
            ? merged.placeholder.trim()
            : DEFAULT_HERO_SEARCH.placeholder;
    const tags = Array.isArray(merged.tags)
        ? merged.tags.filter((tag) => typeof tag === 'string' && tag.trim()).map((tag) => tag.trim())
        : [];

    return {
        enabled: Boolean(merged.enabled),
        placeholder,
        tags,
    };
};

export const isConnectLandingEnabled = (community) => {
    const raw = Array.isArray(community?.optionsRaw) ? community.optionsRaw : [];
    return raw.some(
        (opt) =>
            extractOptionType(opt) === ENABLE_CONNECT_LANDING &&
            extractOptionValue(opt).length > 0
    );
};

export const getConnectLandingOption = (community) => {
    const raw = Array.isArray(community?.optionsRaw) ? community.optionsRaw : [];
    return raw.find((opt) => extractOptionType(opt) === ENABLE_CONNECT_LANDING) || null;
};

export const getConnectLandingConfig = (community) => {
    const option = getConnectLandingOption(community);
    const parsed = safeParseJson(option?.optionValue ?? option?.value);
    const seedConfig = resolveSeedConfig(community, parsed);
    const mergedConfig = mergeNested(seedConfig, parsed);
    const mergedTheme = mergedConfig.theme || {};
    const mergedSections = mergedConfig.sections || {};

    const theme = {
        ...DEFAULT_CONNECT_THEME,
        ...mergedTheme,
        tags: Array.isArray(mergedTheme.tags) && mergedTheme.tags.length
            ? mergedTheme.tags
            : DEFAULT_CONNECT_THEME.tags,
        auroraEnabled: mergedTheme.auroraEnabled !== false,
    };
    const mergedHero = { ...DEFAULT_SECTIONS.hero, ...(mergedSections.hero || {}) };
    const sections = {
        hero: {
            ...mergedHero,
            search: normalizeHeroSearch(mergedHero.search),
        },
        carousel: { ...DEFAULT_SECTIONS.carousel, ...(mergedSections.carousel || {}) },
        process: { ...DEFAULT_SECTIONS.process, ...(mergedSections.process || {}) },
        faq: { ...DEFAULT_SECTIONS.faq, ...(mergedSections.faq || {}) },
        events: { ...DEFAULT_SECTIONS.events, ...(mergedSections.events || {}) },
        opportunities: {
            ...DEFAULT_SECTIONS.opportunities,
            ...(mergedSections.opportunities || {}),
        },
        cta: { ...DEFAULT_SECTIONS.cta, ...(mergedSections.cta || {}) },
    };

    // Logo only from option JSON — missing → null → MiniLogo uses community.logoUrl
    const logo = typeof parsed.logo === 'string' ? parsed.logo.trim() : '';

    const i18n = isPlainObject(mergedConfig.i18n) ? mergedConfig.i18n : {};

    return {
        brandName: mergedConfig.brandName || 'CONSTRUCTOR',
        logo: logo || null,
        theme,
        sections,
        i18n,
    };
};

function prefer(base, override) {
    if (Array.isArray(base)) {
        const overlay = Array.isArray(override) ? override : [];
        return base.map((item, index) => prefer(item, overlay[index]));
    }
    if (base && typeof base === 'object') {
        const source = override && typeof override === 'object' ? override : {};
        const result = {};
        Object.keys(base).forEach((key) => {
            result[key] = prefer(base[key], source[key]);
        });
        return result;
    }
    return typeof override === 'string' && override.trim() ? override : base;
}

export const connectLangChoices = (community) => {
    if (!Array.isArray(community?.langChoices) || community.langChoices.length < 2) return [];
    return community.langChoices
        .map((choice) => String(choice || '').toUpperCase())
        .filter((choice) => choice === 'EN' || choice === 'RU');
};

export const pickConnectLang = (community, storedLang) => {
    const choices = connectLangChoices(community);
    const stored = String(storedLang || '').toUpperCase();
    if (choices.includes(stored)) return stored;
    const primary = String(community?.lang || 'EN').toUpperCase();
    return primary === 'RU' ? 'RU' : 'EN';
};

/** Russian copy overlays English. Blank Russian strings keep the English text. */
export const resolveConnectLandingText = (config, lang) => {
    const selected = String(lang || 'EN').toUpperCase() === 'RU' ? 'RU' : 'EN';
    const overlay = selected === 'RU' ? config?.i18n?.RU : null;
    if (!overlay) return config;
    return {
        ...config,
        brandName: prefer(config.brandName, overlay.brandName),
        sections: prefer(config.sections, overlay.sections),
    };
};

export const CONNECT_THEME_CSS_VARS = [
    ['--connect-bg', 'bg'],
    ['--connect-card-bg', 'cardBg'],
    ['--connect-text', 'text'],
    ['--connect-muted', 'muted'],
    ['--connect-aurora-1', 'aurora1'],
    ['--connect-aurora-2', 'aurora2'],
    ['--connect-aurora-3', 'aurora3'],
    ['--connect-highlight-color', 'highlightColor'],
    ['--connect-highlight-from', 'highlightFrom'],
    ['--connect-highlight-to', 'highlightTo'],
    ['--connect-btn-bg', 'btnBg'],
    ['--connect-btn-text', 'btnText'],
    ['--connect-login-btn-bg', 'loginBtnBg'],
    ['--connect-login-btn-text', 'loginBtnText'],
    ['--connect-login-btn-hover-bg', 'loginBtnHoverBg'],
    ['--connect-secondary-btn-bg', 'secondaryBtnBg'],
    ['--connect-secondary-btn-text', 'secondaryBtnText'],
    ['--connect-secondary-btn-hover-bg', 'secondaryBtnHoverBg'],
    ['--connect-secondary-btn-hover-text', 'secondaryBtnHoverText'],
    ['--connect-secondary-btn-border', 'secondaryBtnBorder'],
    ['--connect-search-border', 'searchBorder'],
    ['--connect-search-border-focus', 'searchBorderFocus'],
    ['--connect-card-border', 'cardBorder'],
    ['--connect-card-border-hover', 'cardBorderHover'],
    ['--connect-card-title-hover', 'cardTitleHover'],
    ['--connect-process-check-bg', 'processCheckBg'],
    ['--connect-process-item-bg', 'processItemBg'],
    ['--connect-process-item-text', 'processItemText'],
    ['--connect-process-item-border', 'processItemBorder'],
    ['--connect-process-item-icon', 'processItemIcon'],
    ['--connect-badge-bg', 'badgeBg'],
    ['--connect-badge-text', 'badgeText'],
    ['--connect-tag-1', 'tags.0'],
    ['--connect-tag-2', 'tags.1'],
    ['--connect-tag-3', 'tags.2'],
    ['--connect-tag-4', 'tags.3'],
];

const resolveThemeValue = (theme, key) => {
    if (key.startsWith('tags.')) {
        const index = Number(key.split('.')[1]);
        return theme.tags?.[index] || DEFAULT_CONNECT_THEME.tags[index] || DEFAULT_CONNECT_THEME.tags[0];
    }
    return theme[key] || DEFAULT_CONNECT_THEME[key];
};

export const applyConnectThemeVars = (theme) => {
    const root = document.documentElement;
    CONNECT_THEME_CSS_VARS.forEach(([cssVar, key]) => {
        root.style.setProperty(cssVar, resolveThemeValue(theme, key));
    });
};

export const clearConnectThemeVars = () => {
    const root = document.documentElement;
    CONNECT_THEME_CSS_VARS.forEach(([cssVar]) => {
        root.style.removeProperty(cssVar);
    });
};
